// AI_CONTEXT: {
//   "docs": [
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-027-mobile-sheet-above-keyboard"
//   ],
//   "purpose": "Base class for modal/overlay views: head/body/foot, backdrop, mobile drawer/floating sheets",
//   "version": "1.3.0",
//   "priority": "core",
//   "stability": "volatile"
// }
/**
 * Overlay - Base class for modal and overlay components
 *
 * Foundation for popup overlays and modals. Managed by Base.js overlay stack,
 * supporting multiple overlays open simultaneously with proper stacking and backdrop.
 *
 * ARCHITECTURE: Extends View (Ancestor → Class → Controller → Module → View → Overlay)
 *
 * INTEGRATION:
 * - Opened via Base.loadOverlay()
 * - Managed in Base.instances.overlays array (stacking support)
 * - Automatic backdrop management
 * - Escape key handling
 * - Mobile-optimized (drawer or floating styles)
 *
 * KEY FEATURES:
 * - Multiple overlay modes (overlay vs modal)
 * - Backdrop with customizable color/opacity
 * - Head/body/foot structure
 * - Closable via action="close" buttons
 * - Mobile-specific styles (drawer, floating)
 * - User avatar and Tixr logo options
 * - Responsive sizing (small, medium)
 * - UX4 modern styling support
 *
 * @class Overlay
 * @extends View
 *
 * @param {Object} setup - Configuration
 * @param {boolean} [setup.closable=true] - Allow closing via close button
 * @param {string} [setup.theme='DARK'] - Theme to apply to overlay container
 * @param {string} [setup.overlayMode='overlay'] - 'overlay' or 'modal'
 * @param {string} [setup.mobileModalStyle='drawer'] - 'drawer' or 'floating'
 * @param {string} [setup.style='legacy'] - 'legacy' or 'ux4'
 * @param {string} [setup.size='medium'] - 'small' or 'medium'
 * @param {boolean} [setup.addBackground=true] - Add backdrop element
 * @param {boolean} [setup.addHead=false] - Add header section
 * @param {boolean} [setup.addFoot=false] - Add footer section
 * @param {boolean} [setup.addBack=false] - Add UX4 back button in head
 * @param {boolean} [setup.showTixrLogo=false] - Show Tixr logo in header
 * @param {boolean} [setup.showAvatar=false] - Show avatar in header
 * @param {string} [setup.hiddenClass='overlay-hidden'] - Class used when hidden
 * @param {boolean} [setup.detectContainerScrollOnMobile=false] - Track scroll on overlay container
 * @param {string} [setup.headCustomClass] - Custom class for UX4 head
 * @param {string} [setup.imageUrl] - Backdrop image URL
 * @param {string|Object|null} [setup.color] - Backdrop color or theme map
 * @param {number} [setup.opacity] - Backdrop opacity
 *
 * @example
 * // Create custom overlay
 * const MyOverlay = Overlay.inherit({
 *     namespace: 'Overlays/MyOverlay',
 *     defaults: {
 *         template: 'Overlays/MyOverlay',
 *         closable: true
 *     }
 * });
 *
 * @example
 * // Open overlay from page/module
 * App.loadOverlay({
 *     type: 'Overlays/MyOverlay',
 *     setup: {
 *         data: 'value'
 *     }
 * });
 */
define("Overlay", [`View`, `module`], function (Super, Self) {
	var OFFSET_AMOUNT = 15;

	return Super.inherit({
		namespace: Self.id,
		requires: [`User`, `Countdown`, `Utils/KeyboardViewport`],
		defaults: {
			hide: true, // start hidden, so it can be transitioned in
			show: false, // allow the application to handle transitioning
			closable: true, // allow closing via action="close" click
			theme: `DARK`,
			addHead: false,
			addFoot: false,
			showTixrLogo: false,
			showAvatar: false,
			hiddenClass: `overlay-hidden`,
			addBackground: true,
			mobileModalStyle: `drawer`, // drawer, floating
			overlayMode: `overlay`, // overlay or modal
			style: `legacy`, // legacy or ux4
			size: `medium`, // small, medium
			addBack: false, // ux4 only - adds the back button to the ux4 head
			detectContainerScrollOnMobile: false, // detect scrolling of the container instead of the app
		},
		prepare(setup) {
			const { data } = this;
			const {
				addHead,
				detectContainerScrollOnMobile,
				showTixrLogo,
				showAvatar,
				closable,
				addBack,
				headCustomClass,
				mobileModalStyle,
			} = setup;
			this.assign(data, {
				addHead,
				detectContainerScrollOnMobile,
				showTixrLogo,
				showAvatar,
				closable,
				addBack,
				headCustomClass,
				mobileModalStyle,
			});
		},
		constructor(setup) {
			const { App, Countdown, DOM, Templates, Utils, container, data, elements, instances } = this;
			const { addHead, detectContainerScrollOnMobile, showAvatar } = data;

			// used for the countdown module
			let cachedExpiration; // used for tracking time that should be used for SimpleCountdown

			this.addClass(`Overlay`); // make sure the container has the right class for styling purposes
			this.addClass(`vp-min-height`);

			this.pushConstructor(() => {
				var background,
					head = this.get(`> .head`),
					body = this.get(`> .body`),
					foot = this.get(`> .foot`);

				if (setup.skipAnimation && this.isElement(body)) {
					body.style.animation = `none`;
				}

				if (setup.overlayMode === `modal`) {
					this.addClass(`modal`);
					this.addClass(`modal-style-${setup.mobileModalStyle}`);

					// CEF-027: keep the mobile bottom sheet above the software keyboard.
					if (setup.mobileModalStyle === `drawer`) {
						this[`Utils/KeyboardViewport`].attach();
					}
				}

				if (setup.style === `ux4`) {
					this.addClass(`ux4`);
					this.addClass(`size-${setup.size}`);
				}

				if (setup.addBackground) {
					background = document.createElement(`div`);
					this.addClass(`overlay-background`, background);
					container.insertBefore(background, container.firstChild);

					if (setup.overlayMode === `modal`) {
						if (setup.theme === `DARK`) {
							this.defaults(setup, {
								color: `17, 17, 17`,
								opacity: 0.8,
								'backdrop-filter': `blur(10px)`,
							});
						} else if (setup.theme === `LIGHT`) {
							this.defaults(setup, {
								color: `#1E1E1E`,
								opacity: 0.8,
								'backdrop-filter': `blur(10px)`,
							});
						}
					} else if (setup.theme === `DARK`) {
						this.defaults(setup, {
							color: `#080808`,
							opacity: 1,
						});
					} else if (setup.theme === `LIGHT`) {
						this.defaults(setup, {
							color: `#F2F4FA`,
							opacity: 1,
						});
					} else {
						throw new Error(`App.showOverlayBackground(): Unrecognized setup.theme`);
					}

					if (this.isString(setup[`backdrop-filter`], 1)) {
						background.style.backdropFilter = setup[`backdrop-filter`];
					}
					const opacity = this.isFinite(setup.opacity, 0) ? setup.opacity : 1;

					if (this.isString(setup.color, 1)) {
						background.style.backgroundColor = Utils.colorToRgba(setup.color, opacity);
					} else if (this.isNull(setup.color)) {
						// alternate way to specify no color
						background.style.backgroundColor = `transparent`;
					} else if (this.isObject(setup.color) && this.isString(setup.theme, 1)) {
						// specify color based on theme
						background.style.backgroundColor = setup.color[setup.theme];
					}

					if (this.isString(setup.imageUrl, 1)) {
						background.style.backgroundImage = `url("${setup.imageUrl}")`;
					}
				}

				if (!this.isElement(head)) {
					head = document.createElement(`div`);

					this.addClass(`head`, head);
					container.prepend(head);
				}

				elements.head = head;

				if (addHead) {
					// ux4 has it's own head markup that sits inside the body instead of the top of the overlay head
					if (setup.style === `ux4`) {
						this.setUx4Head({
							title: setup.title,
							subtitle: setup.subtitle,
						});
					} else {
						this.render({
							namespace: `Overlay`,
							name: `head`,
							target: head,
						});

						if (showAvatar) {
							this.create({
								namespace: `Modules/Avatar`,
								setup: {
									name: `avatar`,
								},
								done: true,
							});
						} else {
							this.hide({
								selector: `[name="avatar"]`,
								done: true,
							});
						}
					}
				}

				if (detectContainerScrollOnMobile) {
					this.addClass(`detect-container-scroll-on-mobile`);

					const scrollHeaderElement = this.get(`.head`);

					if (this.isElement(scrollHeaderElement)) {
						const { IntersectionObserver } = window;
						const handler = ([e]) => {
							if (App.isMobileViewportSize()) {
								return e.target.classList.toggle(`scrolled`, e.intersectionRatio < 1);
							}
						};
						const topThreshold = 15; // slightly below var(--mobile-top-padding)
						const options = { root: container, threshold: [1], rootMargin: `-${topThreshold}px 0px 0px 0px` };
						const observer = new IntersectionObserver(handler, options);

						observer.observe(scrollHeaderElement);

						elements.scrollHeaderElement = scrollHeaderElement;
						instances.observer = observer;
					}
				}

				if (!this.isElement(foot)) {
					foot = document.createElement(`div`);

					this.addClass(`foot`, foot);
					container.insertBefore(foot, body.nextElementSibling);
				}

				if (setup.addFoot) {
					this.render({
						namespace: `Overlay`,
						name: `foot`,
						target: foot,
					});
				}

				Countdown.get({
					fireImmediately: true,
					subscribe: ({ expiration }) => {
						const { countdown } = instances;
						const className = `countdown-present`;
						const name = `simple-countdown`;

						if (this.isInt(expiration)) {
							if (expiration !== cachedExpiration) {
								const timestamp = expiration;

								if (this.isObject(countdown)) {
									countdown.change({ timestamp });
								} else {
									const suffix = null;
									const mode = `bold`;
									const show = true;
									this.create({
										namespace: `Modules/SimpleCountdown`,
										setup: { name, mode, timestamp, suffix, show },
									});
								}
							} // else nothing to do

							this.addClass(className);
						} else {
							this.removeClass(className);
						}
					},
				});
			});

			App.onScroll(({ scrollTop }) => {
				const { data, elements } = this;
				const scrolled = scrollTop > OFFSET_AMOUNT;

				if (data.scrolled !== scrolled) {
					if (scrolled) {
						this.addClass(`scrolled`, elements.head);
					} else {
						this.removeClass(`scrolled`, elements.head);
					}
				}

				data.scrolled = scrolled;
			});

			const href = `/Overlay.css`;
			const namespace = `Overlay`;
			return this.when({
				promises: [DOM.loadStylesheet({ href }), Templates.preload({ namespace })],
			});
		},
		// MARK: API Methods
		api: {
			/**
			 * Set the ux4 head content
			 * @method Overlay#setUx4Head
			 * @param {Object} options - The options will be joined with the data object
			 * @param {string} options.title - The title to display in the head
			 * @param {string} options.subtitle - The subtitle to display in the head
			 * @param {string} [options.headCustomClass] - The custom class to add to the head element (only applies to ux4 style)
			 */
			setUx4Head: {
				public: true,
				value(_, options = {}) {
					const body = this.get(`.body`);
					const { data } = this;
					let ux4Head = this.get(`.head`, body);

					this.assign(data, options);

					if (!this.isElement(ux4Head)) {
						ux4Head = document.createElement(`div`);

						this.addClass(`head`, ux4Head);

						body.prepend(ux4Head); // add as the first child of the body
					}

					if (data.headCustomClass) {
						this.addClass(data.headCustomClass, ux4Head);
					} else {
						ux4Head.className = `head`; // remove any custom classes
					}

					this.render({
						namespace: `Overlay`,
						name: `ux4_head`,
						target: ux4Head,
					});
				},
			},
		},
		// MARK: Event Listeners
		listeners: [
			{
				event: `click`,
				filter: `[action]`,
				target: document.getElementById(`overlay`), // listen on the outer container, so default Overlay container events will fire first
				handler({ currentTarget: link }) {
					const { App, data } = this;
					const action = link.getAttribute(`action`);
					let promise;

					switch (action) {
						case `close`:
							if (data.closable) {
								if (this.isString(data.closeConfirm, 1)) {
									promise = App.askUser({
										message: data.closeConfirm,
									});
								} else {
									promise = true;
								}

								return this.when({
									promise,
									then: (yes) => {
										if (yes) {
											return this.destroy();
										}
									},
								});
							}
							break;
					}
				},
			},
			{
				event: `keyup`,
				target: window,
				matchContainer: false,
				handler({ keyCode }) {
					const { DOM, data } = this;
					const isVisible = DOM.isVisible();

					if (keyCode === 27 && data.closable && isVisible) {
						DOM.trigger({
							event: `click`,
							selector: `[action="close"]`,
						});
					}
				},
			},
			{
				event: `keydown`,
				target: window,
				matchContainer: false,
				handler(event) {
					const { DOM, container } = this;

					if (event.keyCode !== 9) return; // Tab only
					if (!DOM.isVisible()) return;
					if (container.classList.contains(`inactive`)) return;

					const FOCUSABLE = `a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])`;
					const focusable = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
						(el) => el.tabIndex !== -1 && !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
					);

					if (focusable.length === 0) return;

					const first = focusable[0];
					const last = focusable[focusable.length - 1];
					const active = document.activeElement;

					if (event.shiftKey) {
						if (active === first || !container.contains(active)) {
							event.preventDefault();
							last.focus();
						}
					} else {
						if (active === last || !container.contains(active)) {
							event.preventDefault();
							first.focus();
						}
					}
				},
			},
		],
		destructor(options) {
			const { elements, instances } = this;
			const { scrollHeaderElement } = elements;

			this[`Utils/KeyboardViewport`].detach();

			if (this.isElement(scrollHeaderElement)) {
				const { observer } = instances;
				observer.unobserve(scrollHeaderElement);
			}

			this.defaults(options, {
				hide: false, // preserve content, for the sake of the closing overlay animation
				remove: false, // Base.js handles removal of overlay elements
			});
		},
	});
});

define("Overlay.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Overlay/foot"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "";
},"useData":true});

this["Templates"]["Overlay/head"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <a href=\"#\" action=\"close\"> "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"close",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":160},"end":{"line":1,"column":179}}}))
    + " </a> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"background\"></div> <div class=\"content\"> <div name=\"simple-countdown\"></div> <div name=\"avatar\"></div> "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"closable") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":115},"end":{"line":1,"column":192}}})) != null ? stack1 : "")
    + " </div>";
},"useData":true});

this["Templates"]["Overlay/ux4_head"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <a href=\"#\" action=\"back\"> "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"arrow-left",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":65},"end":{"line":1,"column":89}}}))
    + " </a> ";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"subtitle\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"subtitle") : depth0), depth0)) != null ? stack1 : "")
    + "</div> ";
},"5":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <a href=\"#\" action=\"close\"> "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"close",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":289},"end":{"line":1,"column":308}}}))
    + " </a> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"content\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"addBack") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":22},"end":{"line":1,"column":102}}})) != null ? stack1 : "")
    + " <div class=\"overlay-header\"> <div class=\"title\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"subtitle") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":169},"end":{"line":1,"column":236}}})) != null ? stack1 : "")
    + " </div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"closable") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":244},"end":{"line":1,"column":321}}})) != null ? stack1 : "")
    + " </div>";
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Overlay.js.map